/**
 * Landing Page Builder V2 - Constants
 * Component limits, editor behavior, undo/autosave, default colors, and the footer/navbar.
 */

import type { ComponentType } from "../../_types";

/** Maximum number of images allowed in image-based components (Venue, Carousel) */
export const MAX_COMPONENT_IMAGES = 10;

/** Zoom level for the Venue block's static (non-interactive) map */
export const VENUE_MAP_ZOOM = 15;

/** Pixel height of the Venue block's static map (matches the image carousel column) */
export const VENUE_MAP_HEIGHT_PX = 400;

/** Maximum number of each multi-use component type allowed */
export const MAX_MULTI_USE_COMPONENTS = 10;

/** Maximum number of CTA buttons in Hero component */
export const MAX_HERO_CTA_BUTTONS = 3;

/** Maximum number of CTA buttons in Text & Button component */
export const MAX_TEXT_BUTTON_CTA_BUTTONS = 10;

/** Maximum number of buttons in the navbar */
export const MAX_NAVBAR_CTA_BUTTONS = 2;

/**
 * Maximum number of steps one CTA button can run in sequence, counting its own `url` as step 1.
 * Each step is a form or hotel-booking link; see `CtaButton.linkedSteps`.
 */
export const MAX_CTA_LINKED_STEPS = 5;

/**
 * DOM id of the navbar "Buttons" editor section. Shared by NavbarEditor (the element `id`)
 * and NavbarRenderer (the `fieldClick` anchor a canvas button click scrolls to) so they can't drift.
 */
export const NAVBAR_CTA_FIELD_ID = "navbar-cta";

/** Help text shown under the CTA button list in the Hero and Navbar editors. */
export const CTA_LINK_HELP_TEXT =
  "You can link to the Nowadays registration form (opens as a popup), an external website (opens in a new tab), or a section on this page by entering #section-name.";

/** Maximum characters for hero description (plain text, excluding HTML tags) */
export const MAX_HERO_DESCRIPTION_CHARS = 300;

/** Height options for the Hero section — "full" fills the viewport; "banner" is shorter, leaving room to show more of the page below the fold. */
export const HERO_HEIGHT_VARIANT_OPTIONS = [
  { value: "full", label: "Full height" },
  { value: "banner", label: "Banner" },
] as const;
export type HeroHeightVariant = (typeof HERO_HEIGHT_VARIANT_OPTIONS)[number]["value"];

/** Single source of truth for the pre-2026-08 implicit hero height, used by the factory default,
 *  the renderer's destructure default, and the editor's fallback so the three can't drift. */
export const DEFAULT_HERO_HEIGHT_VARIANT: HeroHeightVariant = "full";

/** Tailwind height classes per {@link HeroHeightVariant}, split by whether the sticky navbar is
 *  visible (it eats into the viewport, so the hero fills what's left below it). Class strings must
 *  stay literal here — Tailwind's JIT scanner can't resolve an interpolated arbitrary value, so the
 *  `56px` navbar offset (see {@link NAVBAR_HEIGHT_PX}, the source of truth for that number) is
 *  re-hardcoded per variant rather than interpolated. */
export const HERO_HEIGHT_CLASSES: Record<HeroHeightVariant, { withNavbar: string; withoutNavbar: string }> = {
  full: { withNavbar: "min-h-[calc(100dvh-56px)]", withoutNavbar: "min-h-dvh" },
  // 70dvh leaves ~30% of the viewport for the next section to peek into.
  banner: { withNavbar: "min-h-[calc(70dvh-56px)]", withoutNavbar: "min-h-[70dvh]" },
};

/** Maximum characters for the AI generation modal's "audience" input */
export const AI_GEN_AUDIENCE_MAX_CHARS = 200;

/** Maximum characters for the AI generation modal's "key points" textarea */
export const AI_GEN_KEY_POINTS_MAX_CHARS = 500;

/** Maximum number of team members allowed (e.g. a speakers roster); editor counter + add-button read from this. */
export const MAX_TEAM_MEMBERS = 100;

/** Maximum number of team members shown in the canvas preview */
export const MAX_TEAM_PREVIEW_COUNT = 9;

/** Maximum number of pricing cards allowed */
export const MAX_PRICING_CARDS = 6;

/** Maximum number of cards in a card grid section */
export const MAX_CARD_GRID_ITEMS = 36;

/** Visual layout options for the Card Grid section */
export const CARD_GRID_LAYOUT_OPTIONS = [
  { value: "standard", label: "Standard" },
  { value: "cover", label: "Cover" },
] as const;
export type CardGridLayout = (typeof CARD_GRID_LAYOUT_OPTIONS)[number]["value"];

/** Desktop column options for the Card Grid section. Cover auto mode balances explicit rows. */
export const CARD_GRID_COLUMNS_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
] as const;

/** Description display modes for cover-layout cards */
export const CARD_GRID_DESCRIPTION_DISPLAY_OPTIONS = [
  { value: "always", label: "Always visible" },
  { value: "hover", label: "On hover" },
  { value: "hidden", label: "Hidden" },
] as const;
export type CardGridDescriptionDisplay = (typeof CARD_GRID_DESCRIPTION_DISPLAY_OPTIONS)[number]["value"];

/** Max dimension (px) for cover-layout background images. Tiles render at ~400px × 3-col × 2x DPR. */
export const CARD_COVER_IMAGE_MAX_DIMENSION = 1200;

/** Fixed rendered width (px) of a cover-layout card tile. Constant regardless of card count;
 *  cards reflow/wrap by available width via `repeat(auto-fill, minmax(...))`. Matches the
 *  historical 3-up size inside the 896px (max-w-4xl) section container. */
export const COVER_CARD_WIDTH_PX = 272;

/** Max columns the Card Grid section opens up to at the widest (desktop) breakpoint, for both layouts. */
export const CARD_GRID_MAX_DESKTOP_COLUMNS = 3;

/** Gap (px) between cover-layout card tiles/rows — must match the row's `gap-6` (1.5rem) Tailwind class. */
export const COVER_CARD_GAP_PX = 24;

/** Default secondary color for the custom gradient editor's "to" stop */
export const DEFAULT_CARD_GRADIENT_TO = "#8B5CF6";

// ============================================================
// Divider
// ============================================================

/** Floor clamp (px) applied at render time — guards against a malformed/out-of-range persisted
 *  value (e.g. an unconstrained write from the AI copilot's `height` field). Not itself a choice
 *  offered in the editor; see {@link DIVIDER_HEIGHT_PRESETS} for that. */
export const MIN_DIVIDER_HEIGHT_PX = 8;

/** Ceiling clamp (px) applied at render time — see {@link MIN_DIVIDER_HEIGHT_PX}. */
export const MAX_DIVIDER_HEIGHT_PX = 400;

/** The one place each preset's pixel height is written down. Keyed by name so everything below can
 *  be derived from it — no positional indexing, and no literal to keep in sync. */
const DIVIDER_PRESET_HEIGHTS = { small: 32, medium: 64, large: 128 } as const;

/** Size presets offered in the Divider editor, replacing free-form height entry so every divider
 *  on a page reads as one of a small, consistent set of spacings. */
export const DIVIDER_HEIGHT_PRESETS = [
  { value: "small", label: "Small", height: DIVIDER_PRESET_HEIGHTS.small },
  { value: "medium", label: "Medium", height: DIVIDER_PRESET_HEIGHTS.medium },
  { value: "large", label: "Large", height: DIVIDER_PRESET_HEIGHTS.large },
] as const;
export type DividerHeightPreset = (typeof DIVIDER_HEIGHT_PRESETS)[number]["value"];

/** The preset a new Divider starts on — and the one the editor shows when a stored height can't be
 *  read, so its selected pill always agrees with what the renderer actually draws. */
export const DEFAULT_DIVIDER_HEIGHT_PRESET: DividerHeightPreset = "medium";

/** Default height (px) for a newly-added Divider section — the default preset's own height, so
 *  retuning that preset moves the default with it and reordering the list for display doesn't. */
export const DEFAULT_DIVIDER_HEIGHT_PX = DIVIDER_PRESET_HEIGHTS[DEFAULT_DIVIDER_HEIGHT_PRESET];

/** Default rule thickness (px) when a Divider's line is first turned on */
export const DEFAULT_DIVIDER_LINE_THICKNESS_PX = 1;

/** Thinnest rule the editor offers and the renderer will draw — see {@link MAX_DIVIDER_LINE_THICKNESS_PX}. */
export const MIN_DIVIDER_LINE_THICKNESS_PX = 1;

/** Thickest rule the editor offers and the renderer will draw. Both bounds live here rather than
 *  inline in the editor so the input's `min`/`max`, its hint text, and the render-time clamp can't
 *  drift apart — and so the renderer has a bound to clamp to at all, since `lineThickness` is
 *  copilot-writable and lands in an inline `style`. */
export const MAX_DIVIDER_LINE_THICKNESS_PX = 20;

/** Default rule color when a Divider's line is first turned on */
export const DEFAULT_DIVIDER_LINE_COLOR = "#E5E7EB";

/** Stroke style options for the Divider's visible rule line */
export const DIVIDER_LINE_STYLE_OPTIONS = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
] as const;

/** The allowed rule styles as a lookup set — one derivation shared by the renderer's allowlist and
 *  the save validator, which were each deriving it from {@link DIVIDER_LINE_STYLE_OPTIONS}
 *  separately. A `Set` rather than an object: a plain-object lookup resolves inherited
 *  `Object.prototype` members, so a `lineStyle` of "constructor" reads as a hit. */
export const DIVIDER_LINE_STYLE_VALUES: ReadonlySet<string> = new Set(
  DIVIDER_LINE_STYLE_OPTIONS.map((option) => option.value)
);

/** Maximum number of sponsors per sponsors section */
export const MAX_SPONSORS = 50;

/** Maximum number of tiers in a sponsors section */
export const MAX_SPONSOR_TIERS = 10;

/** Suggested tier labels for sponsor sections, ordered from highest to lowest. */
export const SPONSOR_TIER_PRESETS = ["Presenting", "Platinum", "Gold", "Silver", "Bronze"] as const;

/** Maximum number of features per pricing card */
export const MAX_PRICING_FEATURES = 15;

/** Maximum number of form fields rendered per page in the public-facing form modal */
export const FORM_FIELDS_PER_PAGE = 10;

/** requestIdleCallback timeout (ms) for warming the form-schema cache from SmartButton */
export const FORM_PREFETCH_IDLE_TIMEOUT_MS = 500;

/** requestIdleCallback timeout (ms) for warming the HotelBookingModal chunk from SmartButton/SmartLink */
export const HOTEL_BOOKING_MODAL_PREFETCH_IDLE_TIMEOUT_MS = 4000;

/** How long (ms) after a tap activates a public CTA the click the browser synthesizes from it is ignored */
export const SYNTHETIC_CLICK_WINDOW_MS = 750;

/** Maximum team member cards per row in the canvas (alternates with max - 1) */
export const TEAM_MAX_PER_ROW = 5;

/** Default color palette for labels — cycled when creating new labels */
export const LABEL_COLOR_PALETTE = [
  "#6366f1",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#f97316",
];

/** Periodic autosave interval in milliseconds (backup save with toast notification) */
export const AUTOSAVE_INTERVAL = 15000;

/** Minimum drag distance (px) before a pointer drag activates */
export const DND_ACTIVATION_DISTANCE = 8;

/** Default primary color for new landing pages */
export const DEFAULT_PRIMARY_COLOR = "#5261EB";

/**
 * Default accent color for new landing pages. Opt-in palette color used for highlights
 * (e.g. the countdown digits) — distinct from the primary brand color. Defaults to amber.
 */
export const DEFAULT_ACCENT_COLOR = "#F5A623";

/** Default page/section background color for palette-aware sections. */
export const DEFAULT_BACKGROUND_COLOR = "#FFFFFF";

/** Default body/text color for palette-aware sections. */
export const DEFAULT_TEXT_COLOR = "#0F172A";

/** PostgreSQL unique constraint violation error code */
export const PG_UNIQUE_VIOLATION = "23505";

/** Maximum number of undo history snapshots */
export const MAX_UNDO_HISTORY = 50;

/** Debounce delay for batching continuous edits (e.g. typing) into a single undo step */
export const UNDO_BATCH_DELAY = 500;

/** Toast notification duration (ms) for the landing page builder */
export const BUILDER_TOAST_DURATION = 3000;

/** Default navigation labels for each component type */
export const DEFAULT_NAV_LABELS: Record<ComponentType, string> = {
  navbar: "",
  hero: "Home",
  countdown: "Countdown",
  faq: "FAQ",
  venue: "Venue",
  richText: "About",
  textButton: "Register",
  carousel: "Carousel",
  video: "Video",
  schedule: "Schedule",
  activities: "Activities",
  team: "Team",
  pricing: "Pricing",
  cardGrid: "Cards",
  sponsors: "Sponsors",
  divider: "",
  footer: "",
};

/** Component types that have a dedicated editor in the right sidebar */
export const SUPPORTED_EDITOR_TYPES: ComponentType[] = [
  "navbar",
  "hero",
  "countdown",
  "richText",
  "textButton",
  "faq",
  "venue",
  "carousel",
  "video",
  "schedule",
  "activities",
  "team",
  "pricing",
  "cardGrid",
  "sponsors",
  "divider",
  "footer",
];

/** Component types that can only be added once */
export const SINGLE_USE_COMPONENT_TYPES: ComponentType[] = [
  "hero",
  "countdown",
  "schedule",
  "faq",
  "venue",
  "activities",
  "pricing",
  "footer",
];

// ============================================================
// Footer
// ============================================================

/** Maximum number of manual link columns in a footer */
export const MAX_FOOTER_COLUMNS = 4;

/** Maximum number of links per footer column */
export const MAX_FOOTER_LINKS_PER_COLUMN = 8;

/** Maximum number of social links in a footer */
export const MAX_FOOTER_SOCIAL_LINKS = 10;

/** Default custom background color when switching the footer to "custom" color mode */
export const DEFAULT_FOOTER_CUSTOM_COLOR = "#0F172A";

/** Content alignment options for the Footer section — the same left/center/right set the email
 *  builder's alignment control offers, so the editor can reuse that control as-is. */
export const FOOTER_ALIGNMENT_VALUES = ["left", "center", "right"] as const;
/** One of {@link FOOTER_ALIGNMENT_VALUES}. */
export type FooterAlignment = (typeof FOOTER_ALIGNMENT_VALUES)[number];

/** The footer's layout before alignment was configurable (brand block left, link columns right),
 *  used by the factory default, the renderer's fallback, and the editor's fallback so existing
 *  footers keep rendering exactly as they did. */
export const DEFAULT_FOOTER_ALIGNMENT: FooterAlignment = "left";

/** Tailwind classes per {@link FooterAlignment} for each part of the footer layout. "left" is the
 *  original split layout, "right" mirrors it, and "center" stacks everything in one centered
 *  column (logo/tagline, then link columns, then socials and copyright). */
export const FOOTER_ALIGNMENT_CLASSES: Record<FooterAlignment, { topRow: string; brand: string; bottomRow: string }> = {
  left: {
    topRow: "lg:flex-row lg:items-start lg:justify-between",
    // Without it the logo stretches to its 160px max width and object-contain centers a narrow logo.
    brand: "items-start",
    bottomRow: "justify-between md:flex-row",
  },
  center: {
    topRow: "items-center text-center",
    brand: "items-center",
    bottomRow: "justify-center",
  },
  right: {
    topRow: "text-right lg:flex-row-reverse lg:items-start lg:justify-between",
    brand: "items-end self-end lg:self-auto",
    bottomRow: "justify-between md:flex-row-reverse",
  },
};

// ============================================================
// Navbar
// ============================================================

/** Default custom background color when switching the navbar to "custom" color mode */
export const DEFAULT_NAVBAR_CUSTOM_COLOR = "#ffffff";

/** Default custom button background color when switching the navbar button to "custom" color mode */
export const DEFAULT_NAVBAR_BUTTON_CUSTOM_COLOR = "#5261EB";

/** Height of the navbar top row in pixels — matches `h-14` (3.5rem). Used as the scroll offset for anchor navigation. */
export const NAVBAR_HEIGHT_PX = 56;

/** CSS max-height for the open mobile menu. Uses `dvh` so it respects the visible viewport on iOS (URL bar showing). */
export const MOBILE_MENU_MAX_HEIGHT_CSS = "calc(100dvh - 3.5rem)";

/** Label for the desktop navbar's overflow ("priority+") dropdown that holds links that don't fit inline. */
export const NAV_OVERFLOW_MORE_LABEL = "More";

/** Horizontal gap (px) between items in the desktop nav link row — must match the row's `gap-6` (1.5rem). */
export const NAVBAR_DESKTOP_GAP_PX = 24;

/** Minimum breathing room (px) reserved on each side of the desktop link group (logo↔links and links↔CTA),
 *  so the priority+ measurement collapses links into "More" before the three groups visually touch. */
export const NAVBAR_GROUP_MIN_GAP_PX = 24;

/** Max width (px) of the desktop logo in the condensed (icon) tier — tighter than the wordmark's 160px cap. */
export const NAVBAR_ICON_LOGO_MAX_W_PX = 120;

/** Max width (px) of the desktop logo in the full (wordmark) tier. Matches the legacy `max-w-[160px]`. */
export const NAVBAR_WORDMARK_LOGO_MAX_W_PX = 160;

/** Delay (ms) before a hover-opened navbar dropdown/overflow menu closes after the pointer leaves — gives
 *  the user time to move from the trigger into the panel without it snapping shut. */
export const NAV_DROPDOWN_CLOSE_DELAY_MS = 150;

/** Extra breathing room (px) below the sticky chrome when a navbar dropdown/overflow link anchor-scrolls. */
export const NAV_DROPDOWN_ANCHOR_GAP_PX = 24;

/** Component types that can be added multiple times (up to MAX_MULTI_USE_COMPONENTS) */
export const MULTI_USE_COMPONENT_TYPES: ComponentType[] = [
  "richText",
  "textButton",
  "carousel",
  "video",
  "team",
  "cardGrid",
  "sponsors",
  "divider",
];
